// src/api/customer/customerApi.ts
import type { ApiResponse } from "src/api/common/apiTypes";
import { axiosInstance } from "src/api/axiosInstance";

// Types
export type CustomerStatus = "ACTIVE" | "INACTIVE" | "WITHDRAWN";
export type Gender = "MALE" | "FEMALE" | "";
export type AddressType = "home" | "work" | "etc";
export type PetType = "dog" | "cat";
export type PetGender = "M" | "F";
export type HealthConcernType = "joint" | "skin" | "digestion" | "obesity" | "dental" | "etc";

export interface CustomerListItem {
  id: number;
  username: string;
  email: string;
  phone: string;
  status: CustomerStatus;
  birthDate: string;
  gender: Gender;
  lastLoginAt: string;
  joinedAt: string;
  withdrawYn: boolean;
  deactivated: boolean;
  petCount: number;
  createdAt: string;
}

export interface CustomerAddress {
  id: number;
  addressType: AddressType;
  isDefault: boolean;
  recipientName: string;
  recipientMobile: string;
  postCode: string;
  countryCode?: string;
  city: string;
  district: string;
  address: string;
  additionalAddress?: string;
  deliveryMemo?: string;
}

export interface CustomerPet {
  id: number;
  petName: string;
  petType: PetType;
  petBreedId: number;
  petBreedName: string;
  gender: PetGender;
  birthDate: string;
  adoptionDate?: string;
  weight?: number;
  isNeutered: boolean;
  isAllergy: boolean;
  profileImageUrl?: string;
}

export interface CustomerPetInterest {
  id: number;
  healthConcernType: HealthConcernType;
  healthConcernDesc?: string;
}

export interface CustomerAgreement {
  id: number;
  termsCode: string;
  termsType?: string;
  isAgreed: boolean;
  agreedAt?: string;
  agreedIp?: string;
  agreedDevice?: string;
}

export interface CustomerDetail {
  id: number;
  username: string;
  email: string;
  phone: string;
  status: CustomerStatus;
  birthDate: string;
  gender: Gender;
  lastLoginAt: string;
  joinedAt: string;
  withdrawYn: boolean;
  withdrawAt?: string;
  deactivated: boolean;
  deactivatedAt?: string;
  deactivatedReason?: string;
  createdId: string;
  createdAt: string;
  updatedId: string;
  updatedAt: string;
  addresses: CustomerAddress[];
  pets: CustomerPet[];
  petInterests: CustomerPetInterest[];
  agreements: CustomerAgreement[];
}

export interface CustomerListResponse {
  content: CustomerListItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface GetCustomerListParams {
  username?: string;
  email?: string;
  phone?: string;
  status?: CustomerStatus | "";
  gender?: Gender;
  withdrawYn?: boolean;
  deactivated?: boolean;
  page: number;
  size: number;
  order: string;
}

export interface UpdateCustomerPayload {
  username: string;
  email: string;
  phone: string;
  birthDate: string;
  gender: Gender;
  reason: string;
}

export interface UpdateAddressPayload {
  addressType: AddressType;
  isDefault: boolean;
  recipientName: string;
  recipientMobile: string;
  postCode: string;
  countryCode?: string;
  city: string;
  district: string;
  address: string;
  additionalAddress?: string;
  deliveryMemo?: string;
}

export interface UpdatePetPayload {
  petName: string;
  petType: PetType;
  petBreedId: number;
  gender: PetGender;
  birthDate: string;
  adoptionDate?: string;
  weight?: number;
  isNeutered: boolean;
  isAllergy: boolean;
  profileImageUrl?: string;
}

export interface UpdateAgreementPayload {
  isAgreed: boolean;
}

// API Functions

// 고객 리스트 조회
export const getCustomerListApi = async (
  params: GetCustomerListParams
): Promise<ApiResponse<CustomerListResponse>> => {
  const res = await axiosInstance.get<ApiResponse<CustomerListResponse>>(
    "/v1/admin/customer/user/list",
    { params }
  );
  return res.data;
};

// 고객 상세 조회
export const getCustomerDetailApi = async (id: number): Promise<ApiResponse<CustomerDetail>> => {
  const res = await axiosInstance.get<ApiResponse<CustomerDetail>>(`/v1/admin/customer/user/${id}`);
  return res.data;
};

// 고객 정보 수정
export const updateCustomerApi = async (
  id: number,
  payload: UpdateCustomerPayload
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.put<ApiResponse<null>>(`/v1/admin/customer/user/${id}`, payload);
  return res.data;
};

// 주소 추가
export const createAddressApi = async (
  userId: number,
  payload: UpdateAddressPayload
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.post<ApiResponse<null>>(
    `/v1/admin/customer/user/${userId}/address`,
    payload
  );
  return res.data;
};

// 주소 수정
export const updateAddressApi = async (
  userId: number,
  addressId: number,
  payload: UpdateAddressPayload
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.put<ApiResponse<null>>(
    `/v1/admin/customer/user/${userId}/address/${addressId}`,
    payload
  );
  return res.data;
};

// 주소 삭제
export const deleteAddressApi = async (
  userId: number,
  addressId: number
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(
    `/v1/admin/customer/user/${userId}/address/${addressId}`
  );
  return res.data;
};

// 반려동물 추가
export const createPetApi = async (
  userId: number,
  payload: UpdatePetPayload,
  profileImage?: File
): Promise<ApiResponse<null>> => {
  const formData = new FormData();

  // JSON 데이터를 data 필드로 추가
  formData.append("data", new Blob([JSON.stringify(payload)], { type: "application/json" }));

  // 이미지 파일이 있으면 추가
  if (profileImage) {
    formData.append("profileImage", profileImage);
  }

  const res = await axiosInstance.post<ApiResponse<null>>(
    `/v1/admin/customer/user/${userId}/pet`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return res.data;
};

// 반려동물 수정
export const updatePetApi = async (
  userId: number,
  petId: number,
  payload: UpdatePetPayload,
  profileImage?: File
): Promise<ApiResponse<null>> => {
  const formData = new FormData();

  // JSON 데이터를 data 필드로 추가
  formData.append("data", new Blob([JSON.stringify(payload)], { type: "application/json" }));
  // 이미지 파일이 있으면 추가
  if (profileImage) {
    formData.append("profileImage", profileImage);
  }

  const res = await axiosInstance.put<ApiResponse<null>>(
    `/v1/admin/customer/user/${userId}/pet/${petId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return res.data;
};

// 반려동물 삭제
export const deletePetApi = async (userId: number, petId: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(
    `/v1/admin/customer/user/${userId}/pet/${petId}`
  );
  return res.data;
};

// 약관 동의 수정
export const updateAgreementApi = async (
  userId: number,
  agreementId: number,
  payload: UpdateAgreementPayload
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.put<ApiResponse<null>>(
    `/v1/admin/customer/user/${userId}/agreement/${agreementId}`,
    payload
  );
  return res.data;
};

// 고객 비활성화
export const deactivateCustomerApi = async (id: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.post<ApiResponse<null>>(
    `/v1/admin/customer/user/${id}/deactivate`
  );
  return res.data;
};

// 고객 활성화
export const activateCustomerApi = async (id: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.post<ApiResponse<null>>(`/v1/admin/customer/user/${id}/activate`);
  return res.data;
};

export interface CustomerHistoryItem {
  id: number;
  activityType: string;
  activityTypeName: string;
  beforeData?: Record<string, any>;
  afterData?: Record<string, any>;
  reason?: string;
  memo?: string;
  operatorId: string;
  operatorName: string;
  requestChannel: string;
  createdAt: string;
}

export interface CustomerHistoryResponse {
  content: CustomerHistoryItem[];
  page: {
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface GetCustomerHistoryParams {
  page: number;
  size: number;
}

// 고객 이력 조회
export const getCustomerHistoryApi = async (
  id: number,
  params: GetCustomerHistoryParams
): Promise<ApiResponse<CustomerHistoryResponse>> => {
  const res = await axiosInstance.get<ApiResponse<CustomerHistoryResponse>>(
    `/v1/admin/customer/user/${id}/history`,
    { params }
  );
  return res.data;
};
